import { useTranslation } from "react-i18next"

const defaultTranslations = {
  title: "Hour-reporting workflow",
  description: "Choose how hours move through review and approval.",
  reportingOnlyLabel: "Reporting only",
  reportingOnlyDescription: "Hours remain editable; no review is required.",
  approvalLabel: "Approval",
  approvalDescription: "Hours can be submitted and approved.",
  checkAndApprovalLabel: "Checking and approval",
  checkAndApprovalDescription: "Hours can be submitted, checked, and approved.",
  reviewRequirementsLabel: "Review requirements",
  requireSubmissionBeforeApprovalLabel: "Require submission before approval",
  requireSubmissionBeforeApprovalDescription: "Approvers can only approve hours after the reporter submits them.",
  requireSubmissionBeforeReviewLabel: "Require submission before review",
  requireSubmissionBeforeReviewDescription: "Checkers and approvers cannot act on open hours.",
  requireCheckingBeforeApprovalLabel: "Require checking before approval",
  requireCheckingBeforeApprovalDescription: "Approvers can only approve checked hours.",
  reviewProcessLabel: "What happens to an hour report",
  reporterReports: "Reporter reports",
  reporterMarksReportReady: "Reporter marks ready for review",
  checkerChecksReport: "Checker checks and marks ready for approval",
  approverApprovesReport: "Approval approves the report",
  saveButtonLabel: "Save workflow",
  cancelButtonLabel: "Cancel",
  confirmSaveButtonLabel: "Save workflow",
  confirmSaveTitle: "Save hour report workflow?",
  confirmSaveText:
    "This changes how hour reports are submitted, checked, and approved for everyone in the organization.",
  saveSuccessMessage: "Hour report workflow updated successfully.",
  loadError: "Error loading hour report settings.",
  saveError: "Error updating hour report settings.",
}

export type HourReportWorkflowTranslations = typeof defaultTranslations

export const useHourReportWorkflowTranslations = (): HourReportWorkflowTranslations => {
  const { t } = useTranslation("org")

  return {
    title: t("orgSettingsPage.hourReportWorkflowTitle", { defaultValue: defaultTranslations.title }),
    description: t("orgSettingsPage.hourReportWorkflowDescription", { defaultValue: defaultTranslations.description }),
    reportingOnlyLabel: t("orgSettingsPage.hourReportWorkflowReportingOnlyLabel", {
      defaultValue: defaultTranslations.reportingOnlyLabel,
    }),
    reportingOnlyDescription: t("orgSettingsPage.hourReportWorkflowReportingOnlyDescription", {
      defaultValue: defaultTranslations.reportingOnlyDescription,
    }),
    approvalLabel: t("orgSettingsPage.hourReportWorkflowApprovalLabel", {
      defaultValue: defaultTranslations.approvalLabel,
    }),
    approvalDescription: t("orgSettingsPage.hourReportWorkflowApprovalDescription", {
      defaultValue: defaultTranslations.approvalDescription,
    }),
    checkAndApprovalLabel: t("orgSettingsPage.hourReportWorkflowCheckAndApprovalLabel", {
      defaultValue: defaultTranslations.checkAndApprovalLabel,
    }),
    checkAndApprovalDescription: t("orgSettingsPage.hourReportWorkflowCheckAndApprovalDescription", {
      defaultValue: defaultTranslations.checkAndApprovalDescription,
    }),
    reviewRequirementsLabel: t("orgSettingsPage.hourReportWorkflowReviewRequirementsLabel", {
      defaultValue: defaultTranslations.reviewRequirementsLabel,
    }),
    requireSubmissionBeforeApprovalLabel: t("orgSettingsPage.hourReportWorkflowRequireSubmissionBeforeApprovalLabel", {
      defaultValue: defaultTranslations.requireSubmissionBeforeApprovalLabel,
    }),
    requireSubmissionBeforeApprovalDescription: t(
      "orgSettingsPage.hourReportWorkflowRequireSubmissionBeforeApprovalDescription",
      { defaultValue: defaultTranslations.requireSubmissionBeforeApprovalDescription }
    ),
    requireSubmissionBeforeReviewLabel: t("orgSettingsPage.hourReportWorkflowRequireSubmissionBeforeReviewLabel", {
      defaultValue: defaultTranslations.requireSubmissionBeforeReviewLabel,
    }),
    requireSubmissionBeforeReviewDescription: t(
      "orgSettingsPage.hourReportWorkflowRequireSubmissionBeforeReviewDescription",
      { defaultValue: defaultTranslations.requireSubmissionBeforeReviewDescription }
    ),
    requireCheckingBeforeApprovalLabel: t("orgSettingsPage.hourReportWorkflowRequireCheckingBeforeApprovalLabel", {
      defaultValue: defaultTranslations.requireCheckingBeforeApprovalLabel,
    }),
    requireCheckingBeforeApprovalDescription: t(
      "orgSettingsPage.hourReportWorkflowRequireCheckingBeforeApprovalDescription",
      { defaultValue: defaultTranslations.requireCheckingBeforeApprovalDescription }
    ),
    reviewProcessLabel: t("orgSettingsPage.hourReportWorkflowReviewProcessLabel", {
      defaultValue: defaultTranslations.reviewProcessLabel,
    }),
    reporterReports: t("orgSettingsPage.hourReportWorkflowReporterReports", {
      defaultValue: defaultTranslations.reporterReports,
    }),
    reporterMarksReportReady: t("orgSettingsPage.hourReportWorkflowReporterMarksReportReady", {
      defaultValue: defaultTranslations.reporterMarksReportReady,
    }),
    checkerChecksReport: t("orgSettingsPage.hourReportWorkflowCheckerChecksReport", {
      defaultValue: defaultTranslations.checkerChecksReport,
    }),
    approverApprovesReport: t("orgSettingsPage.hourReportWorkflowApproverApprovesReport", {
      defaultValue: defaultTranslations.approverApprovesReport,
    }),
    saveButtonLabel: t("orgSettingsPage.hourReportWorkflowSaveButtonLabel", {
      defaultValue: defaultTranslations.saveButtonLabel,
    }),
    cancelButtonLabel: t("orgSettingsPage.hourReportWorkflowCancelButtonLabel", {
      defaultValue: defaultTranslations.cancelButtonLabel,
    }),
    confirmSaveButtonLabel: t("orgSettingsPage.hourReportWorkflowConfirmSaveButtonLabel", {
      defaultValue: defaultTranslations.confirmSaveButtonLabel,
    }),
    confirmSaveTitle: t("orgSettingsPage.hourReportWorkflowConfirmSaveTitle", {
      defaultValue: defaultTranslations.confirmSaveTitle,
    }),
    confirmSaveText: t("orgSettingsPage.hourReportWorkflowConfirmSaveText", {
      defaultValue: defaultTranslations.confirmSaveText,
    }),
    saveSuccessMessage: t("orgSettingsPage.hourReportWorkflowSaveSuccessMessage", {
      defaultValue: defaultTranslations.saveSuccessMessage,
    }),
    loadError: t("orgSettingsPage.hourReportsLoadError", { defaultValue: defaultTranslations.loadError }),
    saveError: t("orgSettingsPage.hourReportsSaveError", { defaultValue: defaultTranslations.saveError }),
  }
}

export const useWorkflowRequirementTranslations = () => {
  const { t } = useTranslation("org")

  return {
    requiredStepLabel: t("orgSettingsPage.hourReportWorkflowRequiredStepLabel", { defaultValue: "Required step" }),
    optionalStepLabel: t("orgSettingsPage.hourReportWorkflowOptionalStepLabel", { defaultValue: "Optional step" }),
  }
}

export const useAdminOwnHoursReviewSettingTranslations = () => {
  const { t } = useTranslation("org")

  return {
    label: t("orgSettingsPage.hourReportsAllowAdminsLabel", {
      defaultValue: "Allow hour reporting admins to check and approve their own hours",
    }),
    description: t("orgSettingsPage.hourReportsAllowAdminsDescription", {
      defaultValue: "When disabled, hour reporting admins will not be able to check or approve their own hours.",
    }),
    loadError: t("orgSettingsPage.hourReportsLoadError", {
      defaultValue: "Error loading hour report settings.",
    }),
    saveError: t("orgSettingsPage.hourReportsSaveError", {
      defaultValue: "Error updating hour report settings.",
    }),
  }
}

export const useAdminOwnHoursReviewTranslations = () => {
  const { t } = useTranslation("org")

  return {
    title: t("orgSettingsPage.hourReportAdminsTitle", { defaultValue: "Hour report admins" }),
    viewCurrentAdminsLabel: t("orgSettingsPage.viewCurrentHourReportAdminsLabel", {
      defaultValue: "View current admins",
    }),
  }
}

export const useHourReportingRolesSettingsTranslations = () => {
  const { t } = useTranslation("org")

  return {
    title: t("orgSettingsPage.hourReportingRolesTitle", { defaultValue: "Hour reporting roles" }),
    description: t("orgSettingsPage.hourReportingRolesDescription", {
      defaultValue: "Assign checkers and approvers to reporters to review and approve their hour reports.",
    }),
    viewRolesLabel: t("orgSettingsPage.viewHourReportingRolesLabel", {
      defaultValue: "View hour reporting roles",
    }),
  }
}
